import React, { useState } from 'react';
import type { FC, ReactNode } from 'react';

import PropTypes from 'prop-types';
import { Box, Button, Collapse, ListItem } from '@mui/material';
import type { ListItemProps } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import { Link } from 'react-router-dom';
import { MyDate } from 'utils/shared/MyDate';
import NewFeatureMark from 'presenters/UI/NewFeatureMark';

interface DashboardSidebarItemProps extends ListItemProps {
  active?: boolean;
  children?: ReactNode;
  chip?: ReactNode;
  depth: number;
  icon?: ReactNode;
  info?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  newUntil?: MyDate;
}

export const DashboardSidebarItem: FC<DashboardSidebarItemProps> = (props) => {
  const {
    active = false,
    children,
    chip,
    depth,
    icon,
    info,
    open: openProp = false,
    path,
    title,
    newUntil,
    ...other
  } = props;
  const [open, setOpen] = useState<boolean>(openProp || true);

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 24;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: 'block',
          mb: 0.5,
          py: 0,
          px: 2,
        }}
        {...other}
      >
        <Button
          fullWidth
          endIcon={
            !open ? <ChevronRightIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />
          }
          disableRipple
          onClick={handleToggle}
          // startIcon={icon}
          sx={{
            'color': active ? 'primary.main' : 'text.secondary',
            'justifyContent': 'flex-start',
            'pl': `${paddingLeft}px`,
            'pr': 3,
            'textAlign': 'left',
            'textTransform': 'none',
            'width': '100%',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiButton-startIcon': {
              color: active ? 'secondary.main' : 'text.secondary',
            },
            '& .MuiButton-endIcon': {
              color: 'text.secondary',
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {info}
        </Button>
        <Collapse in={open} sx={{ mt: 0.5 }}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  const isExternal = path?.startsWith('http://') || path?.startsWith('https://');

  const linkProps = isExternal
    ? {
        to: path!,
        target: '_blank', // Opens in a new tab
        rel: 'noopener noreferrer', // Security reasons for external links
      }
    : {
        to: path || '/',
      };

  // Leaf
  return (
    <ListItem
      disableGutters
      sx={{
        display: 'flex',
        mb: 0.5,
        py: 0,
        px: 2,
      }}
    >
      <Link {...linkProps} style={{ textDecoration: 'none', width: '100%' }}>
        <Button
          fullWidth
          startIcon={icon || <ArrowRightIcon fontSize="small" />}
          endIcon={chip}
          disableRipple
          sx={{
            'backgroundColor': active ? 'action.selected' : undefined,
            'borderRadius': 1,
            'color': active ? 'secondary.main' : 'text.secondary',
            'fontWeight': active ? 'fontWeightBold' : undefined,
            'justifyContent': 'flex-start',
            'pl': `${paddingLeft}px`,
            'pr': 3,
            'textAlign': 'left',
            'textTransform': 'none',
            'width': '100%',
            '& .MuiButton-startIcon': {
              color: active ? 'secondary.main' : 'text.secondary',
            },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box sx={{ flexGrow: 1, textDecoration: 'none' }}>
            {' '}
            {title}
            {newUntil && newUntil.toNumber() > new MyDate().toNumber() ? (
              <NewFeatureMark length="long" />
            ) : null}
          </Box>
          {info}
        </Button>
      </Link>
    </ListItem>
  );
};

DashboardSidebarItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
};
